/**
 * Company: SPHMedia
 * Description: International Global Layout
 */

import { ReactElement } from "react";
import { Helmet } from "react-helmet-async";
import { RouteFactory } from "@app/routePaths";
import { VerticalProps } from "@app/types/Verticals";
import PrestitialAdWrapper from "@components/Advertisement/PrestitialAdWrapper";
import Breadcrumb from "@components/Breadcrumb/Breadcrumb";
import Container from "@components/Container/Container";
import Header from "@components/Header/Header";
import {
  BT_NEWSLETTERS,
  NEWSLETTER_TYPE,
} from "@components/Newsletter/types/Newsletter";
import NewsletterSignUpPanel from "@components/Newsletter/Verticals/NewsletterSignUp";
import useGlobalAdSetting from "@hooks/useGlobalAdSetting";
import KeywordStoriesRow from "@pages/Section/components/VerticalMeta/KeywordStoriesRow";
import LatestStoriesRow from "@pages/Section/components/VerticalMeta/LatestStoriesRow";
import TopStoriesRow from "@pages/Section/components/VerticalMeta/TopStoriesRow";
import GlobalEnterpriseBanner from "@pages/Section/layouts/Verticals/GlobalEnterprise/components/GlobalEnterpriseBanner";
import { sectionNavigationItems } from "@util/constant";
import { GoogleAdsSlotFactory } from "@util/helpers";

import GlobalEnterpriseNewsletterDescription from "./components/GlobalEnterpriseNewsletterDescription";

export default function GlobalEnterpriseVertical({
  context,
  mostReadArticles,
}: VerticalProps): ReactElement {
  const {
    data: { title, topStories, additional },
  } = context;
  const { isPrestitialEnabled, topOverlayImpressions, topOverlayValidity } =
    useGlobalAdSetting({
      targeting: { key: "page", value: "listing" },
    });

  return (
    <div className="w-full">
      <Helmet>
        <title>{title}</title>
      </Helmet>

      <PrestitialAdWrapper
        slot={GoogleAdsSlotFactory.prestitial(
          RouteFactory.globalEnterprise.replace("/", "")
        )}
        {...{ isPrestitialEnabled, topOverlayImpressions, topOverlayValidity }}
      />

      <Header />

      <div className="flex-none bg-[var(--digital-breadcrumb-light-gray)]">
        <Breadcrumb
          rootClassName="m-auto container"
          parentCategory="international"
          childCategory="global"
          categoryDataMapping={sectionNavigationItems}
        />
      </div>

      <GlobalEnterpriseBanner />

      <Container rootClassName="mt-6 px-3">
        <TopStoriesRow
          parentCategory="international/global"
          articles={topStories}
          verticalName="ge"
        />

        <LatestStoriesRow
          articles={topStories.slice(4, 9)}
          seeMoreLink="/international/global/latest"
          verticalName="ge"
          mostReadStories={mostReadArticles}
        />

        <KeywordStoriesRow
          seeMoreLink="/keywords/asia-pacific"
          data={additional.at(0)}
          verticalName="ge"
          verticalBlockTitle="ASIA-PACIFIC"
        />
      </Container>

      <NewsletterSignUpPanel
        rootClassName="mb-3"
        descriptionComponent={<GlobalEnterpriseNewsletterDescription />}
        newsletter={BT_NEWSLETTERS.filter(
          (newsletter) =>
            newsletter.type === NEWSLETTER_TYPE.MORNING ||
            newsletter.type === NEWSLETTER_TYPE.EVENING
        )}
        btnColors="bg-verticals-ge text-white hover:bg-white hover:text-verticals-ge"
        linkColors="text-verticals-ge"
      />

      {/* Placeholder for GE footer banner component, will be replaced once done */}
    </div>
  );
}
